import { defineComponent as _defineComponent } from 'vue'

import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useAsync,
  useContext,
  useMeta,
  useStore,
} from "@nuxtjs/composition-api";

import { useAmplitude, useMetaService, useTranslate } from "@/composables";
import { getSharedWishlistsStore } from "@/store/shared-wishlists";
import { formatErrorMessage, handleErrorStatusCode, handleServiceError } from "@/utils/api";
import { NOT_FOUND } from "@/utils/enums/http-codes";
import logger, { logLevel } from "@/utils/logger";

const __default__ = defineComponent({
  name: "Hash",
  middleware: ["shared-collection"],
  head: {},
  fetchDelay: 0,
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

let hash: string;
const paginate = ssrRef(false, "paginate");
const error = ref<unknown | null>(null);
const pending = ssrRef(false, "pending");
const ltkFetchState = computed(() => ({ error: error.value, pending: pending.value }));
const amplitude = useAmplitude();
const context = useContext();
const store = useStore();
const { t } = useTranslate();
const metaService = useMetaService();

useMeta(() => {
  const title = t("Shop curated {categoryName} products on LTK", {
    categoryName: collection?.value?.name ?? "",
  }).value as string;
  const collectionImageHeight = 452;
  const collectionImageWidth = 355;
  const head = metaService.setPageMeta({ title, customTitle: true });
  if (collection?.value?.url) {
    const imageUrl = new URL(collection?.value?.url);
    imageUrl.searchParams.set("w", `${collectionImageWidth}`);
    imageUrl.searchParams.set("h", `${collectionImageHeight}`);
  }
  head.meta?.push(...metaService.setOGImg(collection?.value?.url ?? "", collectionImageWidth, collectionImageHeight));
  return head;
});

useAsync(async () => {
  hash = context.params.value.hash;
  const sharedWishlistsStore = getSharedWishlistsStore(store);
  if (sharedWishlistsStore.collections[hash] === undefined) {
    logger(logLevel.DEBUG, "Fetching missing shared collection", { hash });
    try {
      await sharedWishlistsStore.fetchSharedCollection({ hash });
      if (sharedWishlistsStore.collections[hash] === undefined) {
        logger(logLevel.ERROR, "Shared collection not found", {}, { statusCode: NOT_FOUND });
        return context.error({ statusCode: NOT_FOUND, message: "Shared collection not found" });
      }
    } catch (err) {
      logger(logLevel.ERROR, "Error fetching shared collection", {}, err);
      return context.error({
        statusCode: handleErrorStatusCode(err),
        message: `Error fetching shared collection (${hash}) - ${formatErrorMessage(err)}`,
      });
    }
    try {
      await sharedWishlistsStore.fetchSharedCollectionWishes({ hash });
    } catch (error) {
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(error),
          errorCode: "shared-collection-wishes-not-found",
          developerMessage: `Error fetching shared collection wishes (${hash})`,
        },
        originalError: error,
      });
    }
  }
}, "_hash async");

const totalProducts = computed(() => {
  return collection.value?.wishesCount;
});

const collectionFeed = computed(() => {
  const sharedWishlistsStore = getSharedWishlistsStore(store);
  return sharedWishlistsStore.collectionFeed(hash);
});

const collection = computed(() => {
  const sharedWishlistsStore = getSharedWishlistsStore(store);
  return sharedWishlistsStore.collections[hash];
});

onMounted(() => {
  amplitude?.sendEvent("/page/wishes-by-collection", {
    "wish-collection-display-name": collection.value?.name ?? "",
    "wish-collection-id": collection.value?.id ?? "",
  });
});

const hasNextPage = computed(() => {
  const sharedWishlistsStore = getSharedWishlistsStore(store);
  return sharedWishlistsStore.getFeedHasMorePages(hash);
});

const shareTitle = computed(() => {
  return t("{collectionName} Wishlist on LTK", { collectionName: collection.value?.displayName ?? "" });
});

const shareText = computed(() => {
  return t("Shop the favorites in this {collectionName} Wishlist on LTK.", {
    collectionName: collection.value?.displayName ?? "",
  });
});

const fetch = async () => {
  logger(logLevel.DEBUG, "Fetching missing shared collection wishes", { hash });
  if (paginate.value || collectionFeed.value.ids.length === 0) {
    try {
      pending.value = true;
      const sharedWishlistsStore = getSharedWishlistsStore(store);
      await sharedWishlistsStore.fetchSharedCollectionWishes({ hash });
      pending.value = false;
    } catch (err) {
      error.value = err;
      pending.value = false;
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(err),
          errorCode: "shared-collection-wishes-not-found",
          developerMessage: `Error fetching shared collection wishes (${hash})`,
        },
        originalError: error,
      });
    }
  }
  paginate.value = true;
};

return { __sfc: true,hash, paginate, error, pending, ltkFetchState, amplitude, context, store, t, metaService, totalProducts, collectionFeed, collection, hasNextPage, shareTitle, shareText, fetch }
}

})